/**
 * Role projections — the SAME comprehension fingerprint (mockData.ts), read
 * differently for each audience. The teacher dashboard already reads the roster
 * clinically; these functions derive the STUDENT lens (agency + metacognition)
 * and the PARENT lens (reassurance + context) from the identical per-learner
 * signal, so one class dataset powers all three portals.
 *
 * Everything here is computed from the real `Learner` shape (concept mastery,
 * per-question latency + correctness, dated attempts, watch/replay engagement),
 * so when the live fingerprint API lands the projections are unchanged.
 * No peer comparison is ever produced (minor-safe, per the platform plan).
 */

import type { Learner, ClassRoster, TopicAttempt, ConceptMastery } from './mockData';
import {
  TOPICS, CONCEPTS, conceptMasteryOf, conceptLabel, misconceptionByTag,
} from './mockData';

const lc1 = (s: string) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);

// ---------------------------------------------------------------------------
// Real lessons — map each demo topic to a live catalog experience so "Replay
// lesson" opens the actual interactive video (/learn/:shareToken), not a dead
// button. Tokens are the production catalog's Physics experiences.
// ---------------------------------------------------------------------------

const TOPIC_LESSON: Record<string, string> = {
  electrostatics: 'Y4iwcfHeZLlHbZxb8L7IiA', // Electricity: The Invisible Power (nearest live lesson)
  'current-electricity': 'Y4iwcfHeZLlHbZxb8L7IiA', // Electricity: The Invisible Power
  'ray-optics': 'BqBrABzpVAzsV7GpdprLqg', // Young's Double Slit Experiment
  'em-waves': 'nMLq08CYmg-V297YGCgI5g', // Electromagnetic Waves
};
/** Live catalog shareToken for a topic's lesson, if one exists. */
export const lessonTokenFor = (topicId: string): string | undefined => TOPIC_LESSON[topicId];

// ---------------------------------------------------------------------------
// Bands (own-progress + curriculum norms only — never peer rank)
// ---------------------------------------------------------------------------

export type Tone = 'good' | 'warn' | 'bad';
export interface Band { label: string; tone: Tone }

export function understandingBand(pct: number): Band {
  if (pct >= 70) return { label: 'Strong', tone: 'good' };
  if (pct >= 55) return { label: 'On track', tone: 'warn' };
  return { label: 'Needs support', tone: 'bad' };
}
export const topicBandLabel = (pct: number): string =>
  pct >= 80 ? 'Strong' : pct >= 65 ? 'Good' : pct >= 50 ? 'Working on it' : 'Needs support';

// ---------------------------------------------------------------------------
// Shared learner selectors
// ---------------------------------------------------------------------------

export const masteredConcepts = (l: Learner) => l.conceptMastery.filter((c) => c.state === 'mastered');
export const developingConcepts = (l: Learner) => l.conceptMastery.filter((c) => c.state === 'developing');
export const focusConcepts = (l: Learner) => l.conceptMastery.filter((c) => c.state === 'emerging');
/** Concepts where the learner repeatedly fell for a named distractor (and hasn't mastered it). */
export const flaggedConcepts = (l: Learner) =>
  l.conceptMastery.filter((c) => c.lastMisconceptionTag && c.state !== 'mastered');

export const attemptsByDate = (l: Learner) =>
  [...l.attempts].sort((a, b) => b.submittedDaysAgo - a.submittedDaysAgo); // oldest -> newest

export function lastAttempt(l: Learner): TopicAttempt | undefined {
  if (!l.attempts.length) return undefined;
  return [...l.attempts].sort((a, b) => a.submittedDaysAgo - b.submittedDaysAgo)[0];
}

export function plainMisconception(tag: string, audience: 'student' | 'parent'): string {
  const m = misconceptionByTag(tag);
  if (!m) return '';
  return audience === 'student' ? m.plainStudent : m.plainParent;
}

/** A couple of the learner's strongest concept labels, for warm parent copy. */
export function topStrengthArea(l: Learner): string {
  const strong = masteredConcepts(l).map((c) => conceptLabel(c.conceptTag));
  if (strong.length >= 2) return `${strong[0]} and ${strong[1]}`;
  return strong[0] ?? 'the basics';
}

// ---------------------------------------------------------------------------
// STUDENT · pace & confidence (per-question latency vs first-attempt correctness)
// ---------------------------------------------------------------------------

export interface PaceStripDot { correct: boolean; sec: number }
export interface Pace {
  correctSec: number;
  missedSec: number;
  rushed: number;
  slowerOnMisses: boolean;
  strip: PaceStripDot[];
  insight: string;
}

export function pace(attempt: TopicAttempt): Pace {
  const strip: PaceStripDot[] = attempt.questions.map((q) => ({ correct: q.isCorrect, sec: Math.round(q.latencyMs / 1000) }));
  const cor = strip.filter((s) => s.correct);
  const mis = strip.filter((s) => !s.correct);
  const avg = (a: PaceStripDot[]) => (a.length ? Math.round(a.reduce((s, x) => s + x.sec, 0) / a.length) : 0);
  const correctSec = avg(cor);
  const missedSec = avg(mis);
  const slowerOnMisses = missedSec >= correctSec;
  const rushed = mis.filter((s) => s.sec <= Math.max(6, correctSec - 3)).length;
  const insight = mis.length === 0
    ? 'You answered everything correctly, at a steady and sure pace across the board.'
    : slowerOnMisses
      ? 'You spent longer on the questions you missed than on the ones you got right. That means you were genuinely working them out, so the fix is the idea behind them, not your speed.'
      : 'You spent less time on the questions you missed. When a question feels hard, that is the moment to slow down, not speed up.';
  return { correctSec, missedSec, rushed, slowerOnMisses, strip, insight };
}

// ---------------------------------------------------------------------------
// STUDENT · improvement loop
// ---------------------------------------------------------------------------

export const LOOP_STEPS = ['Learn', 'Test', 'Review the misses', 'Retake'] as const;
/** Which step the learner is on: open gaps -> "Review the misses", else "Retake". */
export const loopStage = (l: Learner): number =>
  (flaggedConcepts(l).length || focusConcepts(l).length ? 2 : 3);

// ---------------------------------------------------------------------------
// STUDENT · next moves (first-person, grounded in exactly what was missed)
// ---------------------------------------------------------------------------

export interface StudentMove {
  severity: 'high' | 'medium' | 'low';
  kind: 'gap' | 'develop' | 'stretch';
  concept?: string;
  topicId?: string;
  title: string;
  body: string;
  steps: string;
}

const topicOfConcept = (tag: string) => CONCEPTS.find((c) => c.tag === tag)?.topicId;

export function studentMoves(l: Learner): StudentMove[] {
  const moves: StudentMove[] = [];
  const gaps = [...focusConcepts(l)].sort((a, b) => (b.lastMisconceptionTag ? 1 : 0) - (a.lastMisconceptionTag ? 1 : 0));
  for (const c of gaps.slice(0, 2)) {
    const hasM = !!c.lastMisconceptionTag;
    moves.push({
      severity: hasM ? 'high' : 'medium', kind: 'gap', concept: conceptLabel(c.conceptTag), topicId: topicOfConcept(c.conceptTag),
      title: `Crack ${conceptLabel(c.conceptTag)}`,
      body: hasM
        ? `A real gap: you scored ${c.correct}/${c.total}, and the misses share one cause, ${lc1(plainMisconception(c.lastMisconceptionTag!, 'student'))} Replay the scene, then retake the questions.`
        : `Your weakest concept so far at ${c.correct}/${c.total}. A short practice set plus a retake will move it up.`,
      steps: hasM ? '2 steps to mastery' : '1 step to mastery',
    });
  }
  for (const c of developingConcepts(l).slice(0, Math.max(0, 2 - moves.length))) {
    const hasM = !!c.lastMisconceptionTag;
    moves.push({
      severity: 'medium', kind: 'develop', concept: conceptLabel(c.conceptTag), topicId: topicOfConcept(c.conceptTag),
      title: `Lock in ${conceptLabel(c.conceptTag)}`,
      body: `You are halfway at ${c.correct}/${c.total}.${hasM ? ` The trap: ${lc1(plainMisconception(c.lastMisconceptionTag!, 'student'))}` : ''} Watch the scene and try two practice items.`,
      steps: '1 step to mastery',
    });
  }
  if (masteredConcepts(l).length >= CONCEPTS.length * 0.6) {
    moves.push({
      severity: 'low', kind: 'stretch', title: 'You are ahead', steps: 'Challenge',
      body: `You have mastered ${masteredConcepts(l).length} of ${CONCEPTS.length} concepts. Try a cross-topic challenge to keep the momentum going.`,
    });
  }
  return moves.slice(0, 3);
}

// ---------------------------------------------------------------------------
// STUDENT/PARENT · mastery trend (cumulative concepts secure over dated attempts)
// ---------------------------------------------------------------------------

export interface TrendPoint { label: string; v: number }
const weekLabel = (d: number) => (d >= 18 ? '3 wks ago' : d >= 12 ? '2 wks ago' : d >= 6 ? '1 wk ago' : 'This week');

export function masteryTrend(l: Learner): TrendPoint[] {
  const byDate = attemptsByDate(l);
  let run = 0;
  return byDate.map((a) => {
    const topic = TOPICS.find((t) => t.id === a.topicId)!;
    run += topic.conceptTags.filter((tag) => conceptMasteryOf(l, tag)?.state === 'mastered').length;
    return { label: weekLabel(a.submittedDaysAgo), v: run };
  });
}

/** What to review after the last test: concepts missed + the plain reason why. */
export interface ReviewItem { conceptTag: string; conceptLabel: string; missed: number; total: number; reason?: string }
export function reviewItems(l: Learner, attempt: TopicAttempt): ReviewItem[] {
  const topic = TOPICS.find((t) => t.id === attempt.topicId)!;
  return topic.conceptTags
    .map((tag) => {
      const qs = attempt.questions.filter((q) => q.conceptTag === tag);
      const missed = qs.filter((q) => !q.isCorrect).length;
      const reasonTag = conceptMasteryOf(l, tag)?.lastMisconceptionTag;
      return {
        conceptTag: tag, conceptLabel: conceptLabel(tag), missed, total: qs.length,
        reason: reasonTag ? plainMisconception(reasonTag, 'student') : undefined,
      };
    })
    .filter((r) => r.missed > 0)
    .sort((a, b) => b.missed - a.missed);
}

// ---------------------------------------------------------------------------
// PARENT · weekly activity + digest
// ---------------------------------------------------------------------------

export interface WeeklyActivity { days: number; minutes: number; streak: number; recent: TopicAttempt[] }
export function weeklyActivity(l: Learner): WeeklyActivity {
  const recent = l.attempts.filter((a) => a.submittedDaysAgo <= 9);
  const minutes = recent.reduce((s, a) => s + a.watchTimeMin + Math.round(a.durationMs / 60000), 0);
  const days = Math.min(Math.max(recent.length, l.lastActiveDaysAgo <= 2 ? 2 : 1), 7);
  const streak = l.lastActiveDaysAgo > 3 ? 0 : l.engagementLevel === 'high' ? 5 : l.engagementLevel === 'medium' ? 3 : 1;
  return { days, minutes, streak, recent };
}

export interface Digest { days: number; minutes: number; finished: string; newSecure: number; reinforce?: string }
export function weeklyDigest(l: Learner): Digest {
  const act = weeklyActivity(l);
  const last = lastAttempt(l);
  const topic = last ? TOPICS.find((t) => t.id === last.topicId) : undefined;
  const newSecure = topic ? topic.conceptTags.filter((tag) => conceptMasteryOf(l, tag)?.state === 'mastered').length : 0;
  const flagged = flaggedConcepts(l);
  const focus = focusConcepts(l);
  const reinforce = flagged.length ? conceptLabel(flagged[0].conceptTag) : focus.length ? conceptLabel(focus[0].conceptTag) : undefined;
  return { days: act.days, minutes: act.minutes, finished: topic?.title ?? '', newSecure, reinforce };
}

// ---------------------------------------------------------------------------
// PARENT · syllabus pace (vs the course plan, never vs other children)
// ---------------------------------------------------------------------------

const SYLLABUS_TOTAL = 14; // CBSE Class XII Physics ~ 14 chapters across the year
export interface SyllabusPace { done: number; total: number; expectedByNow: number; term: string }
export function syllabusPace(l: Learner): SyllabusPace {
  return { done: l.attempts.length, total: SYLLABUS_TOTAL, expectedByNow: TOPICS.length, term: 'CBSE Class XII Physics' };
}

// ---------------------------------------------------------------------------
// PARENT · how you can help at home (misconceptions -> concrete 2-min actions)
// ---------------------------------------------------------------------------

export interface HomeTip { conceptTag?: string; kind: 'reinforce' | 'concrete' | 'celebrate'; title: string; body: string; topicId?: string }
export function atHomeTips(l: Learner): HomeTip[] {
  const tips: HomeTip[] = [];
  const flagged = flaggedConcepts(l);
  for (const c of flagged.slice(0, 2)) {
    const m = c.lastMisconceptionTag ? misconceptionByTag(c.lastMisconceptionTag) : undefined;
    if (!m) continue;
    const topic = CONCEPTS.find((x) => x.tag === c.conceptTag)?.topicId;
    tips.push({
      conceptTag: c.conceptTag, topicId: topic,
      kind: tips.length === 0 ? 'reinforce' : 'concrete',
      title: tips.length === 0 ? `${conceptLabel(c.conceptTag)}, the one to reinforce` : `Make ${conceptLabel(c.conceptTag)} concrete`,
      body: m.atHomeTip,
    });
  }
  if (masteredConcepts(l).length) {
    tips.push({
      kind: 'celebrate', title: 'Lead with the wins',
      body: `${l.firstName} is genuinely strong on ${topStrengthArea(l)}. A word of encouragement about what is going well makes the harder topics feel approachable rather than a chore.`,
    });
  }
  return tips;
}

// ---------------------------------------------------------------------------
// The featured learner for a standalone demo route (prefer the canonical persona)
// ---------------------------------------------------------------------------

export function featuredLearner(roster: ClassRoster): Learner {
  const complete = roster.learners.filter((l) => l.attempts.length === TOPICS.length);
  const aarav = complete.find((l) => l.firstName === 'Aarav');
  if (aarav) return aarav;
  const withStory = complete
    .filter((l) => flaggedConcepts(l).length >= 1 && l.overallMasteryPercent >= 68 && l.overallMasteryPercent <= 88)
    .sort((a, b) => flaggedConcepts(b).length - flaggedConcepts(a).length || b.overallMasteryPercent - a.overallMasteryPercent);
  return withStory[0] ?? complete.sort((a, b) => b.overallMasteryPercent - a.overallMasteryPercent)[0] ?? roster.learners[0];
}

export type { Learner, ClassRoster, ConceptMastery };
